import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslatorService } from './translator.service';
import { ConversationHistoryService } from './conversation-history.service';
import { WordUsageService } from './word-usage.service';

export interface BidirectionalTranscript {
  text: string; // Translated text (what to display)
  originalText: string; // Original transcribed text
  timestamp: Date;
  chunkIndex: number;
  detectedLanguage: string;
  translationDirection: 'source-to-target' | 'target-to-source' | 'none';
}

@Injectable({
  providedIn: 'root',
})
export class BidirectionalConversationService {
  // Reuse same chunking parameters as continuous conversation
  private readonly MIN_CHUNK_DURATION_MS = 1000;
  private readonly MAX_CHUNK_DURATION_MS = 10000;
  private readonly PAUSE_DURATION_MS = 800;
  private readonly VAD_INTERVAL_MS = 100;
  private readonly VAD_RMS_THRESHOLD = 0.01;
  private readonly VAD_PEAK_THRESHOLD = 0.05;
  private readonly DUPLICATE_WINDOW_MS = 10000;
  private readonly MAX_CONCURRENT_REQUESTS = 2;
  private readonly MAX_TRANSCRIPTS = 50;

  private readonly transcripts$ = new BehaviorSubject<BidirectionalTranscript[]>([]);
  private readonly isRecording$ = new BehaviorSubject<boolean>(false);
  private readonly error$ = new BehaviorSubject<string | null>(null);

  private mediaRecorder?: MediaRecorder;
  private audioChunks: Blob[] = [];
  private chunkIndex = 0;
  private sourceLanguage?: string;
  private targetLanguage = 'en';
  private systemPrompt?: string;
  private useAdvancedModel = false;
  private stream?: MediaStream;
  private pendingRequests = 0;
  private recentTranscripts = new Map<string, number>();

  // VAD state (same as continuous conversation)
  private vadAudioContext?: AudioContext;
  private vadAnalyser?: AnalyserNode;
  private vadSource?: MediaStreamAudioSourceNode;
  private vadCheckInterval?: ReturnType<typeof setInterval>;
  private maxChunkTimeout?: ReturnType<typeof setTimeout>;
  private lastSpeechTime = 0;
  private chunkStartTime = 0;
  private isInSpeech = false;

  constructor(
    private readonly translatorService: TranslatorService,
    private readonly conversationHistoryService: ConversationHistoryService,
    private readonly wordUsageService: WordUsageService
  ) {}

  get transcripts(): Observable<BidirectionalTranscript[]> {
    return this.transcripts$.asObservable();
  }

  get isRecording(): Observable<boolean> {
    return this.isRecording$.asObservable();
  }

  get error(): Observable<string | null> {
    return this.error$.asObservable();
  }

  async startRecording(
    sourceLanguage: string,
    targetLanguage: string,
    systemPrompt?: string,
    useAdvancedModel = false
  ): Promise<void> {
    if (this.isRecording$.value) {
      return;
    }

    this.sourceLanguage = sourceLanguage || 'en';
    this.targetLanguage = targetLanguage || 'en';
    this.systemPrompt = systemPrompt;
    this.useAdvancedModel = useAdvancedModel;
    this.recentTranscripts.clear();
    this.audioChunks = [];
    this.error$.next(null);

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
      });

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('[BidirectionalConversation] MediaRecorder error', event);
        this.error$.next('Recording error occurred');
      };

      this.mediaRecorder.onstop = () => {
        if (this.audioChunks.length > 0 && this.isRecording$.value) {
          this.processChunk();
        }
      };

      this.mediaRecorder.start();
      this.isRecording$.next(true);
      this.initializeVAD();
      this.chunkStartTime = Date.now();
      this.scheduleMaxChunkTimeout();
    } catch (err) {
      console.error('[BidirectionalConversation] Failed to start', err);
      this.error$.next(
        err instanceof Error ? err.message : 'Failed to access microphone'
      );
      throw err;
    }
  }

  stopRecording(): void {
    if (!this.isRecording$.value) {
      return;
    }

    this.cleanupVAD();

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      const wasRecording = this.mediaRecorder.state === 'recording';
      try {
        this.mediaRecorder.stop();
      } catch (err) {
        console.warn('[BidirectionalConversation] Error stopping MediaRecorder', err);
      }

      if (wasRecording && this.audioChunks.length > 0) {
        this.processChunk();
      }
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (err) {
          console.warn('[BidirectionalConversation] Error stopping track', err);
        }
      });
      this.stream = undefined;
    }

    if (this.vadSource) {
      try {
        this.vadSource.disconnect();
        this.vadSource = undefined;
      } catch (err) {
        console.warn('[BidirectionalConversation] Error disconnecting VAD source', err);
      }
    }

    if (this.vadAudioContext && this.vadAudioContext.state !== 'closed') {
      try {
        this.vadAudioContext.close();
        this.vadAudioContext = undefined;
      } catch (err) {
        console.warn('[BidirectionalConversation] Error closing VAD AudioContext', err);
      }
    }

    this.mediaRecorder = undefined;
    this.audioChunks = [];
    this.isRecording$.next(false);
    
    // Clear pending transcription requests
    console.log('[BidirectionalConversation] Clearing', this.pendingRequests, 'pending transcription requests');
    this.pendingRequests = 0;
  }

  clearTranscripts(): void {
    this.transcripts$.next([]);
    this.recentTranscripts.clear();
    this.chunkIndex = 0;
  }

  private initializeVAD(): void {
    if (!this.stream) return;

    try {
      this.vadAudioContext = new AudioContext({ sampleRate: 48000 });
      this.vadAnalyser = this.vadAudioContext.createAnalyser();
      this.vadAnalyser.fftSize = 256;
      this.vadSource = this.vadAudioContext.createMediaStreamSource(this.stream);
      this.vadSource.connect(this.vadAnalyser);

      this.vadCheckInterval = setInterval(() => {
        this.checkVAD();
      }, this.VAD_INTERVAL_MS);

      this.scheduleMaxChunkTimeout();
    } catch (error) {
      console.error('[BidirectionalConversation] Error initializing VAD:', error);
    }
  }

  private cleanupVAD(): void {
    if (this.vadCheckInterval) {
      clearInterval(this.vadCheckInterval);
      this.vadCheckInterval = undefined;
    }

    if (this.maxChunkTimeout) {
      clearTimeout(this.maxChunkTimeout);
      this.maxChunkTimeout = undefined;
    }

    if (this.vadSource) {
      this.vadSource.disconnect();
      this.vadSource = undefined;
    }

    if (this.vadAnalyser) {
      this.vadAnalyser.disconnect();
      this.vadAnalyser = undefined;
    }

    if (this.vadAudioContext) {
      this.vadAudioContext.close().catch(console.error);
      this.vadAudioContext = undefined;
    }
  }

  private checkVAD(): void {
    if (!this.vadAnalyser || !this.isRecording$.value) return;

    const bufferLength = this.vadAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.vadAnalyser.getByteTimeDomainData(dataArray);

    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      const normalized = (dataArray[i] - 128) / 128;
      sum += normalized * normalized;
    }
    const rms = Math.sqrt(sum / bufferLength);

    let peak = 0;
    for (let i = 0; i < bufferLength; i++) {
      const normalized = Math.abs((dataArray[i] - 128) / 128);
      if (normalized > peak) peak = normalized;
    }

    const now = Date.now();
    const chunkDuration = now - this.chunkStartTime;
    const timeSinceLastSpeech = now - this.lastSpeechTime;

    const hasSpeech = rms > this.VAD_RMS_THRESHOLD || peak > this.VAD_PEAK_THRESHOLD;

    if (hasSpeech) {
      this.lastSpeechTime = now;
      if (!this.isInSpeech) {
        this.isInSpeech = true;
      }
    } else {
      if (this.isInSpeech) {
        this.isInSpeech = false;
      }
    }

    if (
      !hasSpeech &&
      this.isInSpeech &&
      timeSinceLastSpeech >= this.PAUSE_DURATION_MS &&
      chunkDuration >= this.MIN_CHUNK_DURATION_MS
    ) {
      this.createChunk();
    }
  }

  private createChunk(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
      return;
    }
    this.mediaRecorder.stop();
  }

  private async processChunk(): Promise<void> {
    if (!this.mediaRecorder || !this.isRecording$.value) {
      return;
    }

    const currentChunks = [...this.audioChunks];
    this.audioChunks = [];
    const currentIndex = this.chunkIndex++;

    if (currentChunks.length === 0) {
      if (this.mediaRecorder.state === 'inactive' && this.stream) {
        this.mediaRecorder.start();
      }
      this.chunkStartTime = Date.now();
      this.scheduleMaxChunkTimeout();
      return;
    }

    const audioBlob = new Blob(currentChunks, {
      type: this.mediaRecorder.mimeType,
    });

    if (this.isRecording$.value && this.mediaRecorder.state === 'inactive' && this.stream) {
      try {
        this.mediaRecorder.start();
      } catch (err) {
        console.error('[BidirectionalConversation] Failed to restart MediaRecorder', err);
        this.error$.next('Failed to restart recording');
      }
    }

    this.chunkStartTime = Date.now();
    this.scheduleMaxChunkTimeout();

    if (this.pendingRequests >= this.MAX_CONCURRENT_REQUESTS) {
      console.log(`[BidirectionalConversation] Skipping chunk ${currentIndex} - too many pending requests`);
      return;
    }

    this.transcribeChunk(audioBlob, currentIndex).catch((err) => {
      console.error('[BidirectionalConversation] Transcription failed', err);
      this.error$.next('Failed to transcribe audio chunk');
    });
  }

  private scheduleMaxChunkTimeout(): void {
    if (this.maxChunkTimeout) {
      clearTimeout(this.maxChunkTimeout);
    }

    this.maxChunkTimeout = setTimeout(() => {
      if (this.isRecording$.value && this.mediaRecorder?.state === 'recording') {
        this.createChunk();
      }
    }, this.MAX_CHUNK_DURATION_MS);
  }

  private async transcribeChunk(
    audioBlob: Blob,
    chunkIndex: number
  ): Promise<void> {
    this.pendingRequests++;

    try {
      const timeoutMs = 15000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Transcription timeout')), timeoutMs);
      });

      const MAX_CONTEXT_CHUNKS = 5;
      const currentTranscripts = this.transcripts$.value;
      const conversationContext = currentTranscripts
        .slice(-MAX_CONTEXT_CHUNKS)
        .map((t) => t.originalText)
        .filter((text) => text && text.trim().length > 0);

      const response = await Promise.race([
        this.translatorService.bidirectionalTranscribe(
          audioBlob,
          this.sourceLanguage!,
          this.targetLanguage!,
          this.systemPrompt,
          conversationContext,
          this.useAdvancedModel
        ),
        timeoutPromise,
      ]);

      if (!response.originalText || response.originalText.trim().length === 0) {
        console.log('[BidirectionalConversation] Empty transcription, skipping');
        return;
      }

      // Filter hallucinations
      if (this.isHallucination(response.originalText, this.sourceLanguage)) {
        console.log(`[BidirectionalConversation] 🚫 Filtered hallucination: "${response.originalText}"`);
        return;
      }

      // Also check translated text for hallucinations
      if (response.translatedText && this.isHallucination(response.translatedText, this.sourceLanguage)) {
        console.log(`[BidirectionalConversation] 🚫 Filtered hallucination in translation: "${response.translatedText}"`);
        return;
      }

      const normalizedText = response.originalText.trim().toLowerCase();
      const now = Date.now();
      const lastSeen = this.recentTranscripts.get(normalizedText);

      if (lastSeen && now - lastSeen < this.DUPLICATE_WINDOW_MS) {
        console.log('[BidirectionalConversation] Duplicate detected, skipping');
        return;
      }

      this.recentTranscripts.set(normalizedText, now);
      this.cleanupOldDuplicates();

      // STRICT MODE: If translationDirection is 'none', it means the detected language
      // doesn't match source or target - reject it completely (don't show original text)
      if (response.translationDirection === 'none') {
        console.log(
          `[BidirectionalConversation] 🚫 STRICT MODE: Rejecting transcript - detected language "${response.detectedLanguage}" doesn't match source "${this.sourceLanguage}" or target "${this.targetLanguage}". Original text: "${response.originalText.substring(0, 50)}"`
        );
        return; // Don't add to transcripts
      }

      // Always show translated text if available and not empty
      const hasValidTranslation = 
        response.translatedText && 
        response.translatedText.trim().length > 0;
      
      if (!hasValidTranslation) {
        console.log('[BidirectionalConversation] No valid translation available, skipping');
        return; // Don't add to transcripts if there's no translation
      }
      
      const displayText = response.translatedText.trim();
      
      console.log('[BidirectionalConversation] Display text decision:', {
        translationDirection: response.translationDirection,
        hasTranslatedText: !!response.translatedText,
        translatedTextLength: response.translatedText?.length || 0,
        originalText: response.originalText.substring(0, 30),
        translatedText: response.translatedText?.substring(0, 30) || '(empty)',
        willDisplay: displayText.substring(0, 30),
      });

      const transcripts = [...this.transcripts$.value];
      const newTranscript = {
        text: displayText,
        originalText: response.originalText,
        timestamp: new Date(),
        chunkIndex,
        detectedLanguage: response.detectedLanguage,
        translationDirection: response.translationDirection,
      };
      transcripts.push(newTranscript);

      if (transcripts.length > this.MAX_TRANSCRIPTS) {
        transcripts.shift();
      }

      this.transcripts$.next(transcripts);

      // Save to conversation history
      // Determine userLanguage and targetLanguage based on translation direction
      let userLanguage: string;
      let targetLanguage: string;
      
      if (response.translationDirection === 'source-to-target') {
        // Source language was spoken, translated to target language
        userLanguage = this.sourceLanguage || 'en';
        targetLanguage = this.targetLanguage || 'en';
      } else if (response.translationDirection === 'target-to-source') {
        // Target language was spoken, translated to source language
        userLanguage = this.targetLanguage || 'en';
        targetLanguage = this.sourceLanguage || 'en';
      } else {
        // Skip saving if translation direction is 'none' (rejected transcript)
        return;
      }

      console.log('[BidirectionalConversation] Saving to history:', {
        translationDirection: response.translationDirection,
        userLanguage,
        targetLanguage,
        originalText: response.originalText.substring(0, 50),
        translatedText: displayText.substring(0, 50),
      });

      this.conversationHistoryService.addConversation({
        originalText: response.originalText,
        translatedText: displayText,
        userLanguage,
        targetLanguage,
        confidence: response.confidence || 0,
        inputType: 'speech',
        // Note: Audio URL/blob not available for individual chunks in bidirectional mode
      });

      // Track word usage for transcription and translation
      // Track transcription (original text)
      if (response.originalText && response.originalText.trim().length > 0) {
        // Estimate audio minutes from blob size (similar to regular transcription)
        const audioMinutes = audioBlob.size > 0 
          ? (audioBlob.size / (1024 * 1024)) * 0.1 
          : 0;
        
        this.wordUsageService.trackTranscription(
          response.originalText,
          audioMinutes
        ).subscribe({
          next: (success) => {
            if (success) {
              console.log(
                '[BidirectionalConversation] ✅ Transcription tracked successfully:',
                response.originalText.substring(0, 50)
              );
            }
          },
          error: (error) => {
            console.error('[BidirectionalConversation] ❌ Failed to track transcription:', error);
          }
        });
      }

      // Track translation (translated text)
      if (displayText && displayText.trim().length > 0) {
        this.wordUsageService.trackTranslation(displayText).subscribe({
          next: (success) => {
            if (success) {
              console.log(
                '[BidirectionalConversation] ✅ Translation tracked successfully:',
                displayText.substring(0, 50)
              );
            }
          },
          error: (error) => {
            console.error('[BidirectionalConversation] ❌ Failed to track translation:', error);
          }
        });
      }
    } catch (error) {
      console.error('[BidirectionalConversation] Error transcribing chunk:', error);
      if (error instanceof Error) {
        const errorMessage = error.message || '';
        
        // Check if it's a word limit error - only show those to users
        const isWordLimitError = 
          errorMessage.includes('Word limit exceeded') ||
          errorMessage.includes('Please buy more words') ||
          errorMessage.includes('word limit') ||
          errorMessage.includes('单词限制') || // Chinese
          errorMessage.includes('已使用') || // Chinese "used"
          errorMessage.includes('단어 제한') || // Korean
          errorMessage.includes('사용했습니다') || // Korean "used"
          errorMessage.includes('превышен лимит') || // Russian
          errorMessage.includes('использовано') || // Russian "used"
          errorMessage.includes('сөздүн лимити') || // Kyrgyz
          errorMessage.includes('колдонулду') || // Kyrgyz "used"
          /已使用\s*\d+\s*\/\s*\d+/.test(errorMessage) || // Chinese pattern "已使用 X / Y"
          /已使用\s*\d+\s*，共\s*\d+/.test(errorMessage) || // Chinese pattern "已使用 X，共 Y"
          /使用\s*\d+\s*\/\s*\d+/.test(errorMessage) || // Chinese pattern "使用 X / Y"
          /\d+\s*out\s*of\s*\d+/.test(errorMessage) || // English pattern "X out of Y"
          /\d+\s*\/\s*\d+\s*words/.test(errorMessage) || // Pattern "X / Y words"
          /\d+\s*중\s*\d+/.test(errorMessage) || // Korean pattern "X 중 Y"
          /\d+\s*из\s*\d+/.test(errorMessage); // Russian pattern "X из Y"
        
        if (isWordLimitError) {
          // Only show word limit errors to users
          console.log('[BidirectionalConversation] 💬 Word limit error detected - displaying to user:', errorMessage);
          this.error$.next(errorMessage);
        } else {
          // Log other errors but don't display to user
          console.error('[BidirectionalConversation] 💥 Transcription error (logging only, not displaying to user):', {
            message: errorMessage,
            chunkIndex,
            sourceLanguage: this.sourceLanguage,
            targetLanguage: this.targetLanguage,
          });
        }
      } else {
        // Non-Error objects - log but don't display
        console.error('[BidirectionalConversation] 💥 Transcription error (non-Error) - logging only:', error);
      }
    } finally {
      this.pendingRequests--;
    }
  }

  private cleanupOldDuplicates(): void {
    const now = Date.now();
    for (const [text, timestamp] of this.recentTranscripts.entries()) {
      if (now - timestamp > this.DUPLICATE_WINDOW_MS) {
        this.recentTranscripts.delete(text);
      }
    }
  }

  private isHallucination(text: string, sourceLanguage?: string): boolean {
    const normalizedText = text.toLowerCase().trim();

    // Hallucination patterns by language (same as continuous conversation)
    const hallucinationPatterns: { [key: string]: RegExp[] } = {
      en: [
        /^please\s+speak\s+in\s+[a-zA-Z]+$/i,
        /^speak\s+in\s+[a-zA-Z]+$/i,
        /^please\s+speak\s+only\s+in\s+[a-zA-Z]+$/i,
        /^speak\s+only\s+in\s+[a-zA-Z]+$/i,
        /^please\s+use\s+[a-zA-Z]+$/i,
        /^use\s+[a-zA-Z]+$/i,
        /^Hello,?\s+thank\s+you,?\s+yes,?\s+no\.?$/i,
        /^Hello\.?\s*thank\s+you\.?\s*yes\.?\s*no\.?$/i,
        /^Hello,?\s+thank\s+you,?\s+yes,?\s+no$/i,
        /^Hello,?\s+world!?\.?$/i, // "Hello, world!" - common AI hallucination
        /^Hello[.,;:!?\s]*world[.,;:!?\s]*$/i, // Variant with punctuation
      ],
      ko: [
        /^한국어로\s+말해\s+주세요$/i,
        /^한국어로\s+말하세요$/i,
        /^한국어로만\s+말해\s+주세요$/i,
        /^한국어로만\s+말하세요$/i,
        /^안녕하세요\.?$/i,
        /^안녕하세요,?\s+세상!?\.?$/i, // "Hello, world!" - common AI hallucination
        /^안녕하세요[.,;:!?\s]*세상[.,;:!?\s]*$/i, // Variant with punctuation
      ],
      zh: [
        /^请用中文$/i,
        /^请用中文讲$/i,
        /^请只说中文$/i,
        /^请用中文$/,
        /^请用中文讲$/,
        /^你好[，,]?\s*世界[！!]?\.?$/i, // "你好，世界！" - common AI hallucination
        /^你好[.,;:!?\s]*世界[.,;:!?\s]*$/i, // Variant with punctuation
      ],
      ru: [
        /^пожалуйста\s+говорите\s+по-английски$/i,
        /^говорите\s+по-английски$/i,
        /^говорите\s+только\s+по-английски$/i,
        /^пожалуйста\s+говорите\s+по-русски$/i,
        /^говорите\s+по-русски$/i,
        /^говорите\s+только\s+по-русски$/i,
        /^Привет,?\s+мир!?\.?$/i, // "Привет, мир!" - common AI hallucination
        /^Привет[.,;:!?\s]*мир[.,;:!?\s]*$/i, // Variant with punctuation
      ],
      ky: [
        /^англисче\s+сүйлө\s+бериңиз$/i,
        /^англисче\s+сүйлөңүз$/i,
        /^англисче\s+гана\s+сүйлө\s+бериңиз$/i,
        /^кыргызча\s+сүйлө\s+бериңиз$/i,
        /^кыргызча\s+сүйлөңүз$/i,
        /^Салам,?\s+дүйнө!?\.?$/i, // "Hello, world!" in Kyrgyz
        /^Салам\s+дүйнө$/i, // "Hello world" in Kyrgyz
        /^Саламасызбы\.?$/i, // Common greeting hallucination
        /^Саламасызбы[.,;:!?\s]*$/i, // Variant with punctuation
        /^салам[.,;:!?\s]*дүйнө[.,;:!?\s]*$/i, // Case-insensitive variant
        /^Salamasızby\.?$/i, // Romanized Kyrgyz greeting hallucination
        /^Salamasızby[.,;:!?\s]*$/i, // Variant with punctuation
      ],
      kk: [
        /^ағылшынша\s+айтыңыз$/i,
        /^ағылшынша\s+айтуыңызды\s+сұраймыз$/i,
        /^ағылшынша\s+ғана\s+айтыңыз$/i,
        /^қазақша\s+айтыңыз$/i,
        /^қазақша\s+айтуыңызды\s+сұраймыз$/i,
      ],
      tg: [
        /^лутфан\s+ба\s+англисӣ\s+сухан\s+гӯед$/i,
        /^ба\s+англисӣ\s+сухан\s+гӯед$/i,
        /^танҳо\s+ба\s+англисӣ\s+сухан\s+гӯед$/i,
        /^лутфан\s+ба\s+тоҷикӣ\s+сухан\s+гӯед$/i,
        /^ба\s+тоҷикӣ\s+сухан\s+гӯед$/i,
      ],
      tk: [
        /^haýyş\s+iňlisçe\s+gürleň$/i,
        /^iňlisçe\s+gürleň$/i,
        /^diňe\s+iňlisçe\s+gürleň$/i,
        /^haýyş\s+türkmençe\s+gürleň$/i,
        /^türkmençe\s+gürleň$/i,
      ],
      uz: [
        /^iltimos\s+ingliz\s+tilida\s+gapiring$/i,
        /^ingliz\s+tilida\s+gapiring$/i,
        /^faqat\s+ingliz\s+tilida\s+gapiring$/i,
        /^iltimos\s+o'zbek\s+tilida\s+gapiring$/i,
        /^o'zbek\s+tilida\s+gapiring$/i,
      ],
      fa: [
        /^لطفاً\s+به\s+انگلیسی\s+صحبت\s+کنید$/,
        /^به\s+انگلیسی\s+صحبت\s+کنید$/,
        /^فقط\s+به\s+انگلیسی\s+صحبت\s+کنید$/,
        /^لطفاً\s+به\s+فارسی\s+صحبت\s+کنید$/,
        /^به\s+فارسی\s+صحبت\s+کنید$/,
      ],
      es: [
        /^por\s+favor\s+habla\s+en\s+inglés$/i,
        /^habla\s+en\s+inglés$/i,
        /^por\s+favor\s+habla\s+inglés$/i,
        /^habla\s+inglés$/i,
        /^por\s+favor\s+habla\s+solo\s+en\s+inglés$/i,
        /^habla\s+solo\s+en\s+inglés$/i,
        /^por\s+favor\s+usa\s+inglés$/i,
        /^usa\s+inglés$/i,
        /^por\s+favor\s+habla\s+en\s+español$/i,
        /^habla\s+en\s+español$/i,
        /^por\s+favor\s+habla\s+español$/i,
        /^habla\s+español$/i,
        /^por\s+favor\s+habla\s+solo\s+en\s+español$/i,
        /^habla\s+solo\s+en\s+español$/i,
        /^por\s+favor\s+usa\s+español$/i,
        /^usa\s+español$/i,
        /^¡?Hola,?\s+mundo!?\.?$/i, // "¡Hola, mundo!" - common AI hallucination
        /^Hola[.,;:!?\s]*mundo[.,;:!?\s]*$/i, // Variant with punctuation
      ],
    };

    // Check patterns for the source language
    if (sourceLanguage) {
      const lang = sourceLanguage.toLowerCase();
      const patterns = hallucinationPatterns[lang] || [];
      for (const pattern of patterns) {
        if (pattern.test(normalizedText)) {
          console.log(`[BidirectionalConversation] 🚫 Hallucination detected: "${text}" (matched pattern for ${sourceLanguage})`);
          return true;
        }
      }
    }

    // Also check all languages (as translation might be in target language)
    for (const lang of Object.keys(hallucinationPatterns)) {
      const patterns = hallucinationPatterns[lang];
      for (const pattern of patterns) {
        if (pattern.test(normalizedText)) {
          console.log(`[BidirectionalConversation] 🚫 Hallucination detected: "${text}" (matched pattern for ${lang})`);
          return true;
        }
      }
    }

    // General patterns that apply to all languages
    const generalPatterns = [
      /^please\s+speak\s+in\s+[a-zA-Z]+$/i,
      /^speak\s+in\s+[a-zA-Z]+$/i,
      /^Hello,?\s+thank\s+you,?\s+yes,?\s+no\.?$/i,
      /^Hello\.?\s*thank\s+you\.?\s*yes\.?\s*no\.?$/i,
      /^Hello,?\s+thank\s+you,?\s+yes,?\s+no$/i,
      /^Hello,?\s+world!?\.?$/i, // "Hello, world!" - common AI hallucination
      /^Hello[.,;:!?\s]*world[.,;:!?\s]*$/i, // Variant with punctuation
    ];

    for (const pattern of generalPatterns) {
      if (pattern.test(normalizedText)) {
        console.log(`[BidirectionalConversation] 🚫 Hallucination detected: "${text}" (matched general pattern)`);
        return true;
      }
    }

    // Filter out conversation topic words that are hallucinated back
    if (this.systemPrompt && this.systemPrompt.trim()) {
      const topicWords = this.systemPrompt
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 2); // Only consider words longer than 2 characters

      // Check if transcript text matches or contains topic words as standalone text
      // This filters cases where the topic words are hallucinated back (e.g., "dental lecture" -> "dental" or "lecture")
      if (topicWords.length > 0) {
        // Check if transcript is exactly one of the topic words
        if (topicWords.includes(normalizedText)) {
          console.log(
            `[BidirectionalConversation] 🚫 Filtered topic word hallucination: "${text}" (matches conversation topic word)`
          );
          return true;
        }

        // Check if transcript contains only topic words (e.g., "dental lecture" when topic is "dental lecture")
        const transcriptWords = normalizedText.split(/\s+/).filter((w) => w.length > 0);
        if (
          transcriptWords.length > 0 &&
          transcriptWords.every((word) => topicWords.includes(word)) &&
          transcriptWords.length <= topicWords.length
        ) {
          console.log(
            `[BidirectionalConversation] 🚫 Filtered topic words hallucination: "${text}" (contains only conversation topic words)`
          );
          return true;
        }

        // Check if transcript starts with topic words (e.g., "dental lecture is" when topic is "dental lecture")
        // But only if it's a very short phrase (likely hallucination)
        if (transcriptWords.length <= 3) {
          const startsWithTopic = topicWords.some((topicWord) =>
            normalizedText.startsWith(topicWord)
          );
          if (startsWithTopic && transcriptWords.length <= topicWords.length + 1) {
            console.log(
              `[BidirectionalConversation] 🚫 Filtered topic word at start: "${text}" (starts with conversation topic word)`
            );
            return true;
          }
        }
      }
    }

    return false;
  }
}
